import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { IconLoader2, IconMapPin } from "@tabler/icons-react";
import { Input } from "../common/input/input";
import {
  AddressParser,
  type ParsedAddress,
} from "../../utils/address-parser";

declare global {
  interface Window {
    google?: typeof google;
  }
}

let googleMapsLoader: Promise<void> | null = null;

function loadGoogleMapsPlaces(apiKey: string): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Google Maps can only load in the browser"));
  }

  if (window.google?.maps?.places) {
    return Promise.resolve();
  }

  if (!googleMapsLoader) {
    googleMapsLoader = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(
        'script[data-google-maps-places="true"]',
      );

      if (existing) {
        if (window.google?.maps?.places) {
          resolve();
          return;
        }
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => reject(new Error("Failed to load Google Maps")), {
          once: true,
        });
        return;
      }

      const script = document.createElement("script");
      script.async = true;
      script.defer = true;
      script.dataset.googleMapsPlaces = "true";
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&v=weekly`;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Google Maps"));
      document.head.appendChild(script);
    });
  }

  return googleMapsLoader;
}

type Prediction = google.maps.places.AutocompleteSuggestion;

export interface PlaceAutocompleteProps {
  onPlaceSelect?: (address: ParsedAddress) => void;
  placeholder?: string;
  label?: string;
  helperText?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  name?: string;
}

function formatPrediction(prediction: Prediction): string {
  const placePrediction = prediction.placePrediction;
  const primary = placePrediction?.mainText?.text ?? placePrediction?.text.text ?? "";
  const secondary = placePrediction?.secondaryText?.text ?? "";
  return secondary ? `${primary} · ${secondary}` : primary;
}

export function PlaceAutocomplete({
  onPlaceSelect,
  placeholder = "Search address",
  label = "Address search",
  helperText,
  error,
  disabled = false,
  required = false,
  name = "addressSearch",
}: PlaceAutocompleteProps) {
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const requestSeqRef = useRef(0);
  const blurTimerRef = useRef<number | null>(null);
  const suppressNextFetchRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

  useEffect(() => {
    return () => {
      if (blurTimerRef.current) {
        window.clearTimeout(blurTimerRef.current);
        blurTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!apiKey || disabled) {
      setReady(false);
      setLoading(false);
      return;
    }

    let cancelled = false;

    void loadGoogleMapsPlaces(apiKey)
      .then(async () => {
        if (cancelled) return;

        await google.maps.importLibrary("places");
        sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setReady(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [apiKey, disabled]);

  useEffect(() => {
    if (!ready || disabled) {
      return;
    }

    if (suppressNextFetchRef.current) {
      suppressNextFetchRef.current = false;
      setLoading(false);
      return;
    }

    const trimmed = query.trim();
    if (trimmed.length < 3) {
      setPredictions([]);
      setOpen(false);
      setLoading(false);
      setActiveIndex(-1);
      return;
    }

    const currentRequest = ++requestSeqRef.current;
    const token =
      sessionTokenRef.current ?? new google.maps.places.AutocompleteSessionToken();
    sessionTokenRef.current = token;

    setLoading(true);

    const handle = window.setTimeout(() => {
      void google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: trimmed,
          sessionToken: token,
        })
        .then((response) => {
          if (currentRequest !== requestSeqRef.current) {
            return;
          }

          const nextPredictions = response.suggestions ?? [];
          setPredictions(nextPredictions);
          setOpen(nextPredictions.length > 0);
          setActiveIndex(nextPredictions.length > 0 ? 0 : -1);
        })
        .catch(() => {
          if (currentRequest !== requestSeqRef.current) {
            return;
          }
          setPredictions([]);
          setOpen(false);
          setActiveIndex(-1);
        })
        .finally(() => {
          if (currentRequest === requestSeqRef.current) {
            setLoading(false);
          }
        });
    }, 220);

    return () => {
      window.clearTimeout(handle);
    };
  }, [disabled, query, ready]);

  const selectPrediction = async (prediction: Prediction) => {
    suppressNextFetchRef.current = true;
    setPredictions([]);
    setOpen(false);
    setActiveIndex(-1);
    setLoading(true);

    try {
      const placePrediction = prediction.placePrediction;
      if (!placePrediction) {
        return;
      }

      const place = placePrediction.toPlace();
      await place.fetchFields({ fields: ["addressComponents", "formattedAddress"] });
      setQuery(place.formattedAddress ?? placePrediction.text.text);

      const components = place.addressComponents ?? [];
      const parsed = new AddressParser(
        components.map((component) => ({
          types: component.types ?? [],
          long_name: component.longText ?? "",
        })),
      ).parse();

      onPlaceSelect?.(parsed);
    } finally {
      sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
      setLoading(false);
    }
  };

  const handleBlur = () => {
    blurTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      setActiveIndex(-1);
    }, 120);
  };

  const handleFocus = () => {
    if (predictions.length > 0) {
      setOpen(true);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!open || predictions.length === 0) {
      if (event.key === "Escape") {
        setOpen(false);
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % predictions.length);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => (index <= 0 ? predictions.length - 1 : index - 1));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const selected = predictions[activeIndex] ?? predictions[0];
      if (selected) {
        void selectPrediction(selected);
      }
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  const hasPanel = open && predictions.length > 0;
  const showLoadingHint = ready ? "Start typing an address to see suggestions." : "Loading address lookup...";

  if (!apiKey) {
    return null;
  }

  return (
    <div className="relative">
      <Input
        label={label}
        name={name}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        error={error}
        helperText={helperText ?? (ready ? "Pick a suggestion to autofill the form." : showLoadingHint)}
        autoComplete="off"
        spellCheck={false}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        leftIcon={<IconMapPin size={18} />}
        rightIcon={loading ? <IconLoader2 size={16} className="animate-spin" /> : undefined}
      />

      {hasPanel ? (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 overflow-hidden rounded-2xl border border-borderDefault bg-bgWhite shadow-[0_28px_70px_rgba(4,13,35,0.16)]">
          <div className="flex items-center justify-between border-b border-borderDefault px-4 py-3">
            <span className="text-bodySmBold text-textDark">Suggestions</span>
            <span className="text-bodyXs text-textBody">Google Places</span>
          </div>

          <div className="max-h-72 overflow-auto p-2">
            {predictions.map((prediction, index) => {
              const active = index === activeIndex;
              const placePrediction = prediction.placePrediction;
              const mainText = placePrediction?.mainText?.text ?? placePrediction?.text.text ?? "";
              const secondaryText = placePrediction?.secondaryText?.text ?? formatPrediction(prediction);

              return (
                <button
                  key={placePrediction?.placeId ?? index}
                  type="button"
                  className={[
                    "flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition-colors",
                    active ? "bg-[#EEF4FF]" : "hover:bg-[#F4F7FB]",
                  ].join(" ")}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    void selectPrediction(prediction);
                  }}
                >
                  <span
                    className={[
                      "mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-full",
                      active ? "bg-primary/10 text-primary" : "bg-[#F3F6FB] text-textBody",
                    ].join(" ")}
                  >
                    <IconMapPin size={16} />
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-bodySmBold text-textDark">
                      {mainText}
                    </span>
                    <span className="mt-0.5 block truncate text-bodySm text-textBody">
                      {secondaryText}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="border-t border-borderDefault px-4 py-2">
            <span className="text-bodyXs text-textBody">Powered by Google</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
